/**
 * KNK-1.54 / FR-46 — deactivating and reactivating a rep, and settling the Knock quarantine.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the answers mean is `lib/fieldDeactivation.ts`; who may
 * do what is the database's (migrations 20261010a1, c3, d3). Errors are thrown with the server's
 * own message, because every refusal there is a sentence written for the person reading it —
 * "Deactivating a rep needs Manage leads for Everyone." is more use than anything this file
 * could invent.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  parseDeactivationRows,
  parsePreview,
  parseQuarantine,
  type DeactivationPreview,
  type DeactivationRow,
  type Quarantine,
} from '@/lib/fieldDeactivation';

/** What a deactivation would move, and who could take it. `lead.manage` + Everyone scope. */
export async function fetchDeactivationPreview(userId: string): Promise<DeactivationPreview> {
  const { data, error } = await supabase.rpc('deactivate_field_rep_preview', { p_user: userId });
  if (error) throw new Error(error.message);
  const preview = parsePreview(data);
  if (!preview) throw new Error('The preview came back in a shape this screen does not understand.');
  return preview;
}

/**
 * Deactivate, moving the open Revisits and standing Claims to `toRepId`.
 *
 * ⚠️ `toRepId` may be null ONLY when there is nothing to move; the server refuses otherwise and
 * names the counts. The screen reads that from the preview rather than deciding here.
 */
export async function deactivateFieldRep(userId: string, toRepId: string | null): Promise<void> {
  const { error } = await supabase.rpc('deactivate_field_rep', { p_user: userId, p_to_rep: toRepId });
  if (error) throw new Error(error.message);
}

/** ⚠️ Refused while any Knock is still held. What moved does NOT move back. */
export async function reactivateFieldRep(userId: string): Promise<void> {
  const { error } = await supabase.rpc('reactivate_field_rep', { p_user: userId });
  if (error) throw new Error(error.message);
}

export async function fetchQuarantine(deactivationId: string): Promise<Quarantine> {
  const { data, error } = await supabase.rpc('field_deactivation_quarantine', { p_deactivation: deactivationId });
  if (error) throw new Error(error.message);
  const quarantine = parseQuarantine(data);
  if (!quarantine) throw new Error('The quarantine came back in a shape this screen does not understand.');
  return quarantine;
}

/**
 * Accept or discard held Knocks. Returns how many the server actually resolved, which can be
 * fewer than were sent: an id already settled by someone else is skipped rather than answered
 * with another row's state.
 */
export async function resolveQuarantine(
  deactivationId: string,
  knockIds: string[],
  action: 'accept' | 'discard',
): Promise<{ resolved: number; remaining: number }> {
  const { data, error } = await supabase.rpc('resolve_field_deactivation_quarantine', {
    p_deactivation: deactivationId,
    p_knock_ids: knockIds,
    p_action: action,
  });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    resolved: typeof r.resolved === 'number' ? r.resolved : 0,
    remaining: typeof r.remaining === 'number' ? r.remaining : 0,
  };
}

/** Every deactivation in the account, open first. */
export async function fetchDeactivations(): Promise<DeactivationRow[]> {
  const { data, error } = await supabase.rpc('field_deactivations_list');
  if (error) throw new Error(error.message);
  return parseDeactivationRows(data);
}
